import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Network, Truck, Plug, RefreshCw, MessageSquareText, ChevronsUpDown, CalendarClock, ShieldAlert, LogOut, Crown, Factory, ScanLine, FileCheck2, Car, MonitorPlay } from "lucide-react";
import AskDotIQ from "./AskDotIQ";
import { api } from "../services/api";
import { useAuth } from "../context/AuthContext";

const NAV = {
  kajaria: [
    { to: "/", label: "Executive Dashboard", icon: LayoutDashboard, end: true },
    { to: "/insights", label: "Explainable Insights", icon: Sparkles },
    { to: "/graph", label: "Context Graph", icon: Network },
    { to: "/fulfillment", label: "Fulfillment Navigator", icon: Truck },
    { to: "/connectors", label: "Integration Engine", icon: Plug },
  ],
  vans: [
    { to: "/vans", label: "Decisions to Take Today", icon: Sparkles, end: true },
    { to: "/vans/graph", label: "Context Graph", icon: Network },
    { to: "/vans/connectors", label: "Integration Engine", icon: Plug },
  ],
  igl: [
    { to: "/igl", label: "Decisions to Take Today", icon: ShieldAlert, end: true },
    { to: "/igl/graph", label: "Relationship Graph", icon: Network },
    { to: "/igl/connectors", label: "Integration Engine", icon: Plug },
  ],
  dalmia: [
    { to: "/dalmia", label: "Executive Dashboard", icon: LayoutDashboard, end: true },
    { to: "/dalmia/insights", label: "Explainable Insights", icon: Factory },
    { to: "/dalmia/graph", label: "Relationship Graph", icon: Network },
    { to: "/dalmia/fulfillment", label: "Fulfillment Navigator", icon: Truck },
    { to: "/dalmia/connectors", label: "Integration Engine", icon: Plug },
  ],
  iglrv: [
    { to: "/iglrv", label: "Safety Command Center", icon: ShieldAlert, end: true },
    { to: "/iglrv/operator", label: "Operator Board", icon: MonitorPlay },
    { to: "/iglrv/anpr", label: "ANPR Lane", icon: ScanLine },
    { to: "/iglrv/fleet", label: "Fleet Roster", icon: Car },
    { to: "/iglrv/prevalidate", label: "Driver / Fleet Pre-validation", icon: FileCheck2 },
    { to: "/iglrv/connectors", label: "Integration Engine", icon: Plug },
  ],
};

const BRANDS = {
  kajaria: { name: "Kajaria Ceramics", tag: "SupplySense", sub: "Fulfillment Intelligence", home: "/" },
  vans: { name: "Vans · VF Corp", tag: "Demand→Inventory", sub: "Demand Signal → Inventory Action", home: "/vans" },
  igl: { name: "IGL", tag: "Safety & Compliance", sub: "Intelligent Safety, Compliance & Operations", home: "/igl" },
  dalmia: { name: "Dalmia Bharat", tag: "Fulfillment & Margin", sub: "Intelligent Fulfillment + Margin Early Warning", home: "/dalmia" },
  iglrv: { name: "IGL · Refuel Validation", tag: "CNG Pre-Check", sub: "Pre-emptive CNG Vehicle Refuel Validation", home: "/iglrv" },
};

export default function Layout({ children }) {
  const [askOpen, setAskOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [today, setToday] = useState("");
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => { api.root().then((d) => setToday(d.today)).catch(() => {}); }, []);

  const brand = loc.pathname.startsWith("/vans") ? "vans" : loc.pathname.startsWith("/iglrv") ? "iglrv" : loc.pathname.startsWith("/igl") ? "igl" : loc.pathname.startsWith("/dalmia") ? "dalmia" : "kajaria";
  const nav = NAV[brand];
  const b = BRANDS[brand];
  const current = nav.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to) && n.to !== b.home)) ||
    nav.find((n) => n.to === b.home);

  const reseed = async () => {
    setSeeding(true);
    try { await api.seed(); window.location.reload(); } finally { setSeeding(false); }
  };

  const switchBrand = (key) => { setSwitcherOpen(false); if (key !== brand) navigate(BRANDS[key].home); };

  return (
    <div className={`app-shell brand-${brand}`}>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">Dot<span>IQ</span></div>
          <div className="brand-sub">Correlation Intelligence</div>
        </div>

        <div className="brand-switch">
          <button className="brand-switch-btn" onClick={() => setSwitcherOpen((v) => !v)}>
            <div className="bs-left">
              <span className="bs-dot" />
              <div>
                <div className="bs-name">{b.name}</div>
                <div className="bs-tag">{b.tag}</div>
              </div>
            </div>
            <ChevronsUpDown size={15} />
          </button>
          {switcherOpen && (
            <div className="brand-menu">
              {Object.entries(BRANDS).map(([key, val]) => (
                <button key={key} className={`brand-menu-item ${key === brand ? "active" : ""}`} onClick={() => switchBrand(key)}>
                  <span className="bs-dot" />
                  <div>
                    <div className="bs-name">{val.name}</div>
                    <div className="bs-tag">Design Partner · {val.tag}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="nav">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
              <n.icon size={18} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={({ isActive }) => `nav-item admin-nav ${isActive ? "active" : ""}`}>
              <Crown size={18} />
              <span>Admin Console</span>
            </NavLink>
          )}
        </nav>

        <div className="sidebar-foot">
          {user && (
            <div className="user-chip">
              {user.picture
                ? <img src={user.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                : <span className="user-avatar fallback">{(user.name || user.email || "?")[0].toUpperCase()}</span>}
              <div className="user-meta">
                <div className="user-name">{user.name || user.email}
                  {user.role === "admin" && <span className="role-badge admin sm"><Crown size={10} /> admin</span>}
                </div>
                <div className="user-email">{user.email}</div>
              </div>
              <button className="icon-btn" title="Sign out" onClick={logout}><LogOut size={15} /></button>
            </div>
          )}
          <div className="loop">
            <div className="loop-steps">CONNECT · DETECT · CORRELATE · RANK · EXPLAIN</div>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <h1 className="topbar-title">{current?.label || "DotIQ"}</h1>
            <p className="topbar-desc">{b.sub}</p>
          </div>
          <div className="topbar-actions">
            <span className="date-badge"><CalendarClock size={13} /> As of {today || "—"}</span>
            {brand === "kajaria" && (
              <button className="btn-ghost" onClick={reseed} disabled={seeding}>
                <RefreshCw size={15} className={seeding ? "spin" : ""} /> <span>Re-run pipeline</span>
              </button>
            )}
            <button className="btn-primary" onClick={() => setAskOpen(true)}>
              <MessageSquareText size={16} /> Ask DotIQ
            </button>
          </div>
        </header>
        <div className="content">{children}</div>
      </main>

      <AskDotIQ open={askOpen} onClose={() => setAskOpen(false)} brand={brand} />
    </div>
  );
}
